import * as React           from 'react'
import { FormattedMessage } from 'react-intl'

import Button      from '@material-ui/core/Button'
import Card        from '@material-ui/core/Card'
import CardActions from '@material-ui/core/CardActions'
import CardContent from '@material-ui/core/CardContent'
import CardHeader  from '@material-ui/core/CardHeader'

import {
  canInteractWithTripartita,
  isStudent,
  isTeacher,
}                                           from '../../project-utils/permissions'
import { Communication, TripartitaPackage } from '../../redux/ducks'
import pickCurried                          from '../../utils/pick-curried'
import { PermissionGuard }                  from '../atoms'
import TripartitaFollowupForm               from '../forms/TripartitaFollowupForm'
import TripartitaStudentInitialForm         from '../forms/TripartitaStudentInitialForm'
import TripartitaTeacherInitialForm         from '../forms/TripartitaTeacherInitialForm'
import withToggle, { InjectedToggleProps }  from '../hocs/withToggle'
import LabelledData                         from './LabelledData'

interface NextTripartitaProps {
  isInitialTripartitaUp: boolean
  isIntermediateTripartitaUp: boolean
  isFinalTripartitaUp: boolean

  onComplete: (values: any) => void
  onEdit: (values: any) => void

  initialUpdateCommunication: Communication
  intermediateUpdateCommunication: Communication
  finalUpdateCommunication: Communication

  tripartita: TripartitaPackage
}

type Props =
  NextTripartitaProps
  & InjectedToggleProps

const pickTripartitaProps = pickCurried([
  'indicators',
  'challenge',
  'objectives',
  'superiorCommitment',
  'commitment',
])

const NextTripartita: React.SFC<Props> = ({
  isInitialTripartitaUp,
  isIntermediateTripartitaUp,
  isFinalTripartitaUp,

  initialUpdateCommunication,
  intermediateUpdateCommunication,
  finalUpdateCommunication,

  isActive,
  activate,
  deactivate,

  onComplete,
  onEdit,

  tripartita,
}) => {
  const phrasing = {
    nextTripartita:         <FormattedMessage id={'generic.nextTripartita'} />,
    edit:                   <FormattedMessage id={'buttonActions.edit'} />,
    complete:               <FormattedMessage id={'buttonActions.confirmCompletion'} />,
    initialTripartita:      <FormattedMessage id={'project.initialTripartita'} />,
    intermediateTripartita: <FormattedMessage id={'project.intermediateTripartita'} />,
    finalTripartita:        <FormattedMessage id={'project.finalTripartita'} />,
  }

  if (isInitialTripartitaUp) {
    const tripartitaData = pickTripartitaProps(tripartita.initial)

    return (
      <>
        <Card>
          <CardHeader
            title={phrasing.nextTripartita}
            subheader={phrasing.initialTripartita}
          />
          {tripartitaData && Boolean(Object.keys(tripartitaData).length) && (
            <CardContent>
              <LabelledData data={tripartitaData} />
            </CardContent>
          )}
          <PermissionGuard check={canInteractWithTripartita}>
            <CardActions>
              <PermissionGuard check={isStudent}>
                <Button
                  color={'primary'}
                  onClick={activate}
                >
                  {phrasing.edit}
                </Button>
              </PermissionGuard>
              <PermissionGuard check={isTeacher}>
                <Button
                  color={'primary'}
                  onClick={activate}
                >
                  {phrasing.complete}
                </Button>
              </PermissionGuard>
            </CardActions>
          </PermissionGuard>
        </Card>
        <PermissionGuard check={isTeacher}>
          <TripartitaTeacherInitialForm
            isOpen={isActive}
            close={deactivate}
            onSubmit={onComplete}
            {...initialUpdateCommunication}
          />
        </PermissionGuard>
        <PermissionGuard check={isStudent}>
          <TripartitaStudentInitialForm
            isOpen={isActive}
            close={deactivate}
            onSubmit={(...args) => {
              onEdit(...args)
              deactivate()
            }}
            {...initialUpdateCommunication}
          />
        </PermissionGuard>
      </>
    )
  }

  if (isIntermediateTripartitaUp || isFinalTripartitaUp) {
    const tripartitaData =
      isIntermediateTripartitaUp
        ? pickTripartitaProps(tripartita.intermediate)
        : pickTripartitaProps(tripartita.final)

    return (
      <>
        <Card>
          <CardHeader
            title={phrasing.nextTripartita}
            subheader={
              isIntermediateTripartitaUp
                ? phrasing.intermediateTripartita
                : phrasing.finalTripartita
            }
          />
          {tripartitaData && Boolean(Object.keys(tripartitaData).length) && (
            <CardContent>
              <LabelledData data={tripartitaData} />
            </CardContent>
          )}
          <PermissionGuard check={[ canInteractWithTripartita, isTeacher ]}>
            <CardActions>
              <Button
                color={'primary'}
                onClick={activate}
              >
                {phrasing.complete}
              </Button>
            </CardActions>
          </PermissionGuard>
        </Card>
        <TripartitaFollowupForm
          isOpen={isActive}
          close={deactivate}
          onSubmit={onComplete}
          fetching={intermediateUpdateCommunication.fetching || finalUpdateCommunication.fetching}
          error={intermediateUpdateCommunication.error || finalUpdateCommunication.error}
          success={intermediateUpdateCommunication.success || finalUpdateCommunication.success}
        />
      </>
    )
  }

  return null
}

export default withToggle(NextTripartita)



// WEBPACK FOOTER //
// ./src/components/molecules/NextTripartita.tsx